import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { useCancelMyWalletRedemptionHoldMutation } from "@/generated/graphql";

interface CancelPendingHoldButtonProps {
  /** Called after a successful cancel — refetch wallet/user data here. */
  onCancelled?: () => void | Promise<void>;
  size?: "sm" | "default";
  className?: string;
}

/**
 * Cancels the user's pending wallet-redemption hold (plan or extra minutes),
 * releasing the reserved balance. Only redemption holds are cancellable —
 * pending PIX withdrawals stay admin-managed, so render this button only when
 * `myCpmWallet.pending_kind` is `plan_redemption` or `minutes_redemption`.
 */
export function CancelPendingHoldButton({ onCancelled, size = "sm", className }: CancelPendingHoldButtonProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelHold] = useCancelMyWalletRedemptionHoldMutation();

  const handleConfirm = async () => {
    setCancelling(true);
    try {
      await cancelHold();
      toast({ title: t('wallet.holdCancelledTitle'), description: t('wallet.holdCancelledDesc') });
      setConfirmOpen(false);
      await onCancelled?.();
    } catch (error: any) {
      const message: string = error?.message || '';
      const known: Record<string, string> = {
        remainder_already_paid: t('wallet.holdErrorAlreadyPaid'),
        hold_cancel_failed: t('wallet.holdErrorCancelFailed'),
        no_pending_redemption: t('wallet.holdErrorNothingPending'),
        withdrawal_not_cancellable: t('wallet.holdErrorWithdrawal'),
      };
      const match = Object.keys(known).find((code) => message.includes(code));
      toast({
        title: t('wallet.holdCancelErrorTitle'),
        description: match ? known[match] : t('wallet.holdErrorCancelFailed'),
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size={size}
        // Amber styling lives here (not at call sites) so every surface
        // renders identically; explicit hover text/bg because the outline
        // variant's hover:bg-accent/hover:text-accent-foreground would
        // otherwise wash the amber out on hover.
        className={cn(
          "border-amber-500/40 text-amber-600 dark:text-amber-400",
          "hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-300",
          className,
        )}
        onClick={() => setConfirmOpen(true)}
      >
        <XCircle className="w-4 h-4 mr-1.5" />
        {t('wallet.cancelHoldButton')}
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && cancelling) return;
          setConfirmOpen(open);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('wallet.cancelHoldConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('wallet.cancelHoldConfirmDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={cancelling}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={cancelling}>
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('wallet.cancelHoldConfirmButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
